import React, { useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthProvider';
import LoginModal from './components/LoginModal';
import NotificationModal from './components/NotificationModal';
import './App.css';

// Lazy-loaded route components
const DeckBuilder = lazy(() => import('./components/DeckBuilder'));
const DeckView = lazy(() => import('./components/DeckView'));
const Auth = lazy(() => import('./components/Auth'));
const DeckTierPage = lazy(() => import('./components/DeckTierPage'));
const CardTierPage = lazy(() => import('./components/CardTierPage'));
const Proxies = lazy(() => import('./components/Proxies'));

// ScrollToTop component to handle scroll restoration
function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Main app content
function AppContent({ onShowLoginModal }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      scrollRestoration="manual"
    >
      <AppRoutes
        isAuthenticated={isAuthenticated}
        onShowLoginModal={onShowLoginModal}
      />
    </Router>
  );
}

// Routes component that handles authentication logic
function AppRoutes({ isAuthenticated, onShowLoginModal }) {
  const location = useLocation();

  // Check if the current route is a deck viewing route
  const isDeckViewRoute = location.pathname.startsWith('/decks/') || location.pathname.startsWith('/deck/');

  // Allow public access to deck viewing routes and main deck builder
  if (!isAuthenticated && !isDeckViewRoute && location.pathname !== '/') {
    return (
      <Suspense fallback={<div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>}>
        <Auth />
      </Suspense>
    );
  }

  return (
    <>
      <ScrollToTop />
      <div className="App">
        <main className="app-main">
          <Suspense fallback={<div className="loading-container"><div className="loading-spinner"></div><p>Loading...</p></div>}>
            <Routes>
              <Route path="/" element={<DeckBuilder onShowLoginModal={onShowLoginModal} />} />
              <Route path="/decks/:identifier" element={<DeckView />} />
              <Route path="/deck/:identifier" element={<DeckView />} />
              <Route path="/decktier" element={<DeckTierPage />} />
              <Route path="/cardtier" element={<CardTierPage />} />
              <Route path="/cardtier/:slug" element={<CardTierPage />} />
              <Route path="/proxies" element={<Proxies />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </>
  );
}

function App() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  const handleNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };

  const hideNotification = () => {
    setNotification({ show: false, message: '', type: 'success' });
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    // Scroll to top when login is successful, especially important for mobile
    window.scrollTo(0, 0);
  };

  return (
    <AuthProvider onNotification={handleNotification}>
      <AppContent
        onShowLoginModal={() => setShowLoginModal(true)}
      />
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
      <NotificationModal
        notification={notification}
        onClose={hideNotification}
      />
    </AuthProvider>
  );
}

export default App;
